import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Printer, Copy, Eye } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface ContractVerificationFormProps {
  dealTitle?: string;
  sellerName?: string;
  buyerName?: string;
  buyerAddress?: string;
}

interface VerificationData {
  hasOriginalContract: boolean;
  buyerName: string;
  buyerPhone: string;
  buyerAddress: string;
  additionalNotes: string;
}

const ContractVerificationForm: React.FC<ContractVerificationFormProps> = ({
  dealTitle,
  sellerName,
  buyerName,
  buyerAddress
}) => {
  const { toast } = useToast();
  const [showPreview, setShowPreview] = useState(false);
  
  const [formData, setFormData] = useState<VerificationData>({
    hasOriginalContract: false,
    buyerName: buyerName || '',
    buyerPhone: '',
    buyerAddress: buyerAddress || '',
    additionalNotes: ''
  });

  useEffect(() => {
    if (buyerName) setFormData(prev => ({ ...prev, buyerName }));
    if (buyerAddress) setFormData(prev => ({ ...prev, buyerAddress }));
  }, [buyerName, buyerAddress]);

  const updateField = (field: keyof VerificationData, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getVerificationText = () => {
    let text = `CONTRACT VERIFICATION FORM\n`;
    text += `Contract Funding Group\n\n`;
    text += `Date: ${new Date().toLocaleDateString()}\n\n`;
    if (sellerName) text += `Seller: ${sellerName}\n\n`;
    
    text += `ORIGINAL CONTRACT VERIFICATION\n`;
    text += `☐ ${formData.hasOriginalContract ? '✓' : ' '} Seller confirms they have the original signed ink contract in their possession\n\n`;
    
    text += `BUYER CONTACT INFORMATION\n`;
    text += `Name: ${formData.buyerName}\n`;
    text += `Phone: ${formData.buyerPhone}\n`;
    text += `Address: ${formData.buyerAddress}\n\n`;
    
    if (formData.additionalNotes) {
      text += `ADDITIONAL NOTES\n${formData.additionalNotes}\n\n`;
    }
    
    text += `I verify that the above information is accurate and complete.\n\n`;
    text += `Signature: _________________________  Date: _____________\n\n`;
    text += `Contract Funding Group`;
    
    return text;
  };

  const handleCopyToClipboard = () => {
    const text = getVerificationText();
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied to clipboard",
      description: "Verification form copied and ready to paste"
    });
  };

  const handlePrint = () => {
    window.print();
    toast({
      title: "Print Dialog Opened",
      description: "The verification form is ready to print."
    });
  };

  const handlePrintBlank = () => {
    setShowPreview(false);
    setTimeout(() => {
      const printContent = document.getElementById('blank-print-version');
      const printWindow = window.open('', '', 'height=800,width=800');
      if (printWindow && printContent) {
        printWindow.document.write('<html><head><title>Contract Verification Form</title>');
        printWindow.document.write('<style>');
        printWindow.document.write('body { font-family: Arial, sans-serif; padding: 30px; margin: 0; }');
        printWindow.document.write('h1 { text-align: center; margin-bottom: 5px; font-size: 20px; }');
        printWindow.document.write('h2 { text-align: center; font-size: 16px; margin-bottom: 3px; }');
        printWindow.document.write('.date { text-align: center; font-size: 12px; color: #666; margin-bottom: 20px; }');
        printWindow.document.write('.section { margin-bottom: 20px; padding: 15px; border: 1px solid #333; border-radius: 5px; }');
        printWindow.document.write('.checkbox-section { margin-bottom: 20px; padding: 15px; border: 1px solid #333; border-radius: 5px; }');
        printWindow.document.write('.checkbox { display: inline-block; width: 16px; height: 16px; border: 2px solid #000; margin-right: 8px; vertical-align: middle; }');
        printWindow.document.write('.field { margin-bottom: 12px; display: flex; align-items: center; }');
        printWindow.document.write('.field strong { min-width: 120px; }');
        printWindow.document.write('.field-line { flex: 1; border-bottom: 1px solid #000; min-height: 20px; }');
        printWindow.document.write('.signature-section { margin-top: 25px; display: grid; grid-template-columns: 1fr 1fr; gap: 30px; }');
        printWindow.document.write('.signature-line { border-bottom: 1px solid #000; height: 30px; margin-top: 15px; }');
        printWindow.document.write('</style>');
        printWindow.document.write('</head><body>');
        printWindow.document.write(printContent.innerHTML);
        printWindow.document.write('</body></html>');
        printWindow.document.close();
        printWindow.print();
      }
    }, 100);
  };

  const VerificationPreview = () => (
    <div className="max-w-2xl mx-auto p-8 bg-background">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold">Contract Funding Group</h1>
        <p className="text-lg font-semibold mt-2">Contract Verification Form</p>
        <p className="text-sm text-muted-foreground mt-1">{new Date().toLocaleDateString()}</p>
      </div>

      {sellerName && (
        <div className="mb-6 p-4 bg-muted rounded-lg">
          <p>Seller: {sellerName}</p>
        </div>
      )}

      <div className="mb-8 p-4 border-2 rounded-lg">
        <h3 className="text-lg font-semibold mb-3">Original Contract Verification</h3>
        <div className="flex items-center gap-3">
          <div className={`w-5 h-5 border-2 border-foreground flex items-center justify-center ${formData.hasOriginalContract ? 'bg-foreground' : ''}`}>
            {formData.hasOriginalContract && <span className="text-background font-bold">✓</span>}
          </div>
          <p className="text-sm">
            Seller confirms they have the original signed ink contract in their possession
          </p>
        </div>
      </div>

      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-4">Buyer Contact Information</h3>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-4">
            <div className="font-medium">Name:</div>
            <div className="col-span-2">{formData.buyerName || '_________________'}</div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="font-medium">Phone:</div>
            <div className="col-span-2">{formData.buyerPhone || '_________________'}</div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="font-medium">Address:</div>
            <div className="col-span-2 whitespace-pre-wrap">{formData.buyerAddress || '_________________'}</div>
          </div>
        </div>
      </div>

      {formData.additionalNotes && (
        <div className="mb-8 p-4 bg-muted rounded-lg">
          <h3 className="text-lg font-semibold mb-2">Additional Notes</h3>
          <p className="whitespace-pre-wrap text-sm">{formData.additionalNotes}</p>
        </div>
      )}

      <div className="mt-12 pt-6 border-t">
        <p className="mb-8">I verify that the above information is accurate and complete.</p>
        <div className="grid grid-cols-2 gap-8">
          <div>
            <p className="mb-2">Signature:</p>
            <div className="border-b-2 border-foreground h-8"></div>
          </div>
          <div>
            <p className="mb-2">Date:</p>
            <div className="border-b-2 border-foreground h-8"></div>
          </div>
        </div>
      </div>
    </div>
  );

  const BlankFormPreview = () => (
    <div id="blank-print-version" style={{ display: 'none' }}>
      <h1>Contract Funding Group</h1>
      <h2>Contract Verification Form</h2>
      <div className="date">{new Date().toLocaleDateString()}</div>

      {sellerName && (
        <div style={{ marginBottom: '15px' }}>
          <div><strong>Seller:</strong> {sellerName}</div>
        </div>
      )}

      <div className="checkbox-section">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span className="checkbox"></span>
          <span>Seller has original signed contract in possession</span>
        </div>
      </div>

      <div className="section">
        <h3 style={{ marginBottom: '15px' }}>Buyer Contact Information</h3>
        
        <div className="field">
          <strong>Name:</strong> <span className="field-line"></span>
        </div>

        <div className="field">
          <strong>Phone:</strong> <span className="field-line"></span>
        </div>

        <div className="field">
          <strong>Address:</strong> <span className="field-line"></span>
        </div>
      </div>

      <div style={{ marginTop: '30px' }}>
        <div>I verify the above information is accurate.</div>
        <div className="signature-section">
          <div>
            <div>Signature:</div>
            <div className="signature-line"></div>
          </div>
          <div>
            <div>Date:</div>
            <div className="signature-line"></div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <BlankFormPreview />
      <Card>
        <CardHeader>
          <CardTitle>Contract Verification</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="p-4 border rounded-lg bg-muted/50">
            <div className="flex items-start gap-3">
              <Checkbox
                id="hasOriginalContract"
                checked={formData.hasOriginalContract}
                onCheckedChange={(checked) => updateField('hasOriginalContract', checked as boolean)}
                className="mt-1"
              />
              <Label htmlFor="hasOriginalContract" className="cursor-pointer">
                Seller confirms they have the original signed ink contract in their possession
              </Label>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Buyer Contact Information</h3>
            
            <div>
              <Label htmlFor="buyerName">Buyer Name *</Label>
              <Input
                id="buyerName"
                value={formData.buyerName}
                onChange={(e) => updateField('buyerName', e.target.value)}
                placeholder="Full name of buyer"
              />
            </div>

            <div>
              <Label htmlFor="buyerPhone">Phone *</Label>
              <Input
                id="buyerPhone"
                type="tel"
                value={formData.buyerPhone}
                onChange={(e) => updateField('buyerPhone', e.target.value)}
                placeholder="(555) 555-5555"
              />
            </div>

            <div>
              <Label htmlFor="buyerAddress">Address *</Label>
              <Textarea
                id="buyerAddress"
                value={formData.buyerAddress}
                onChange={(e) => updateField('buyerAddress', e.target.value)}
                placeholder="Full mailing address"
                rows={3}
              />
            </div>

            <div>
              <Label htmlFor="additionalNotes">Additional Notes</Label>
              <Textarea
                id="additionalNotes"
                value={formData.additionalNotes}
                onChange={(e) => updateField('additionalNotes', e.target.value)}
                placeholder="Any additional information for the bank..."
                rows={3}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3 flex-wrap">
            <Button onClick={() => setShowPreview(true)} variant="outline">
              <Eye className="h-4 w-4 mr-2" />
              Preview
            </Button>
            
            <Button onClick={handleCopyToClipboard} variant="outline">
              <Copy className="h-4 w-4 mr-2" />
              Copy to Clipboard
            </Button>
            
            <Button onClick={handlePrint} variant="outline">
              <Printer className="h-4 w-4 mr-2" />
              Print Filled
            </Button>

            <Button onClick={handlePrintBlank} variant="outline">
              <Printer className="h-4 w-4 mr-2" />
              Print Blank Form
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Contract Verification Form Preview</DialogTitle>
          </DialogHeader>
          <VerificationPreview />
        </DialogContent>
      </Dialog>

      {/* Hidden print version */}
      <div className="hidden print:block">
        <VerificationPreview />
      </div>
    </div>
  );
};

export default ContractVerificationForm;
